import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Input } from '../../primitives/input';
import { cn } from '../../utils';
import type { ColumnDef, ColumnFilter } from './types';

// ---------------------------------------------------------------------------
// deriveOptions — auto-build select options from live data
// ---------------------------------------------------------------------------

export function deriveOptions<T>(
  data: T[],
  col: ColumnDef<T>,
): Array<{ value: string; label: string }> {
  const seen = new Set<string>();
  const opts: Array<{ value: string; label: string }> = [];
  for (const row of data) {
    const columnKey = col.id ?? String(col.key ?? '');
    const raw = col.accessor ? col.accessor(row) : (row as Record<string, unknown>)[columnKey];
    const s = String(raw ?? '');
    if (!seen.has(s)) {
      seen.add(s);
      opts.push({ value: s, label: s });
    }
    if (opts.length >= 50) break;
  }
  return opts.sort((a, b) => a.label.localeCompare(b.label));
}

// ---------------------------------------------------------------------------
// Draft-state helpers
// ---------------------------------------------------------------------------

type DraftText = { type: 'text'; value: string };
type DraftSelect = { type: 'select'; values: string[] };
type DraftRange = { type: 'range'; min: string; max: string };
type DraftDate = { type: 'date'; from: string; to: string };
type Draft = DraftText | DraftSelect | DraftRange | DraftDate;

function initDraft(current: ColumnFilter | undefined, filterType: ColumnFilter['type']): Draft {
  if (!current) {
    if (filterType === 'text') return { type: 'text', value: '' };
    if (filterType === 'select') return { type: 'select', values: [] };
    if (filterType === 'range') return { type: 'range', min: '', max: '' };
    return { type: 'date', from: '', to: '' };
  }
  switch (current.type) {
    case 'text':
      return { type: 'text', value: current.value };
    case 'select':
      return { type: 'select', values: [...current.values] };
    case 'range':
      return {
        type: 'range',
        min: current.min !== null ? String(current.min) : '',
        max: current.max !== null ? String(current.max) : '',
      };
    case 'date':
      return { type: 'date', from: current.from ?? '', to: current.to ?? '' };
  }
}

function draftToFilter(draft: Draft): ColumnFilter | null {
  switch (draft.type) {
    case 'text':
      return draft.value === '' ? null : { type: 'text', value: draft.value };
    case 'select':
      return draft.values.length === 0 ? null : { type: 'select', values: draft.values };
    case 'range': {
      const min = draft.min === '' ? null : Number(draft.min);
      const max = draft.max === '' ? null : Number(draft.max);
      return min === null && max === null ? null : { type: 'range', min, max };
    }
    case 'date':
      return draft.from === '' && draft.to === ''
        ? null
        : { type: 'date', from: draft.from || null, to: draft.to || null };
  }
}

// ---------------------------------------------------------------------------
// FilterPopover
// ---------------------------------------------------------------------------

export interface FilterPopoverProps<T> {
  col: ColumnDef<T>;
  data: T[];
  anchorEl: HTMLElement | null;
  current: ColumnFilter | undefined;
  onApply: (filter: ColumnFilter | null) => void;
  onClose: () => void;
}

export function FilterPopover<T>({
  col,
  data,
  anchorEl,
  current,
  onApply,
  onClose,
}: FilterPopoverProps<T>) {
  const filterType = col.filter?.type ?? 'text';
  const [draft, setDraft] = useState<Draft>(() => initDraft(current, filterType));
  const popoverRef = useRef<HTMLDivElement>(null);

  // Position relative to anchor
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    setPos({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
    });
  }, [anchorEl]);

  // Outside-click + Escape
  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  function apply() {
    onApply(draftToFilter(draft));
    onClose();
  }

  function reset() {
    onApply(null);
    onClose();
  }

  const colLabel = col.label ?? (typeof col.header === 'string' ? col.header : col.id);

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={`Filter ${colLabel}`}
      style={{ top: pos.top, left: pos.left, animationDuration: '100ms' }}
      className={cn(
        'fixed z-[150] min-w-[200px] max-w-[280px]',
        'rounded-[6px] border border-[var(--color-border)]',
        'bg-[var(--color-bg)] shadow-[0_4px_16px_rgba(0,0,0,0.12)]',
        'animate-in fade-in slide-in-from-top-1',
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--color-border)]">
        <span className="text-[12px] font-medium text-[var(--color-text-secondary)] uppercase tracking-[0.04em]">
          {colLabel}
        </span>
        <button
          type="button"
          aria-label="Clear filter and close"
          onClick={reset}
          className="inline-flex items-center justify-center size-5 rounded-[2px] text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] transition-colors"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      </div>

      {/* Body */}
      <div className="px-3 py-2">
        {draft.type === 'text' && (
          <Input
            autoFocus
            placeholder={col.filter?.placeholder ?? 'Filter…'}
            value={draft.value}
            onChange={(e) => setDraft({ type: 'text', value: e.currentTarget.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') apply();
            }}
            className="h-8 text-[13px]"
          />
        )}

        {draft.type === 'select' && (
          <div className="max-h-[160px] overflow-y-auto">
            {(col.filter?.options ?? deriveOptions(data, col)).map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 py-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.values.includes(opt.value)}
                  onChange={() => {
                    const next = draft.values.includes(opt.value)
                      ? draft.values.filter((v) => v !== opt.value)
                      : [...draft.values, opt.value];
                    setDraft({ type: 'select', values: next });
                  }}
                  className="accent-[var(--color-accent)]"
                />
                <span className="text-[13px] text-[var(--color-text-primary)]">{opt.label}</span>
              </label>
            ))}
          </div>
        )}

        {draft.type === 'range' && (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              placeholder="Min"
              value={draft.min}
              onChange={(e) =>
                setDraft((d) => ({ ...(d as DraftRange), min: e.currentTarget.value }))
              }
              className="h-8 w-[88px] text-[13px]"
            />
            <span className="text-[var(--color-text-tertiary)]">–</span>
            <Input
              type="number"
              placeholder="Max"
              value={draft.max}
              onChange={(e) =>
                setDraft((d) => ({ ...(d as DraftRange), max: e.currentTarget.value }))
              }
              className="h-8 w-[88px] text-[13px]"
            />
          </div>
        )}

        {draft.type === 'date' && (
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={draft.from}
              onChange={(e) =>
                setDraft((d) => ({ ...(d as DraftDate), from: e.currentTarget.value }))
              }
              className="h-8 text-[13px]"
            />
            <span className="text-[var(--color-text-tertiary)]">–</span>
            <Input
              type="date"
              value={draft.to}
              onChange={(e) =>
                setDraft((d) => ({ ...(d as DraftDate), to: e.currentTarget.value }))
              }
              className="h-8 text-[13px]"
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-[var(--color-border)]">
        <button
          type="button"
          onClick={reset}
          className={cn(
            'h-7 px-2 rounded-[4px] text-[12px]',
            'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
            'border border-[var(--color-border)] transition-colors',
          )}
        >
          Reset
        </button>
        <button
          type="button"
          onClick={apply}
          className={cn(
            'h-7 px-3 rounded-[4px] text-[12px] font-medium',
            'bg-[var(--color-accent)] text-[var(--color-bg)]',
            'hover:bg-[var(--color-accent-hover)] transition-colors',
          )}
        >
          Apply
        </button>
      </div>
    </div>
  );
}
